/**
 * Parse Lead Delta / LinkedIn-style CSV and map rows to `linkedin` table shape.
 * Primary key is `linkedin_slug` (LinkedIn /in/ vanity name), extracted from the profile URL.
 * Multiline quoted fields are supported; UTF-8 BOM on first header is stripped.
 */

/** Match linkedin.com/in/{slug} (handles www, regional subdomains, http/https). */
const LINKEDIN_IN_PATH =
  /^(?:https?:\/\/)?(?:[\w.-]+\.)?linkedin\.com\/in\/([^/?#]+)/i;

export function extractLinkedinProfileSlug(input: string | null | undefined): string | null {
  if (input == null) return null;
  const u = input.trim();
  if (!u) return null;
  let m = u.match(LINKEDIN_IN_PATH);
  if (!m?.[1]) {
    m = u.match(/^in\/([^/?#]+)/i);
  }
  if (!m?.[1]) return null;
  let slug = m[1].replace(/\/$/, '');
  try {
    slug = decodeURIComponent(slug);
  } catch {
    /* keep raw */
  }
  slug = slug.trim().toLowerCase();
  return slug.length ? slug : null;
}

export type LinkedinUpsertRow = {
  linkedin_slug: string;
  lead_delta_id: string | null;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  linkedin_url: string | null;
  email: string | null;
  business_email: string | null;
  personal_email: string | null;
  phone: string | null;
  headline: string | null;
  company_name: string | null;
  location: string | null;
  country: string | null;
  industry: string | null;
  connected_on: string | null;
  followers: number | null;
  languages: string | null;
  date_of_birth: string | null;
  last_messaged: string | null;
  tags: string | null;
  notes: string | null;
  profile_picture_url: string | null;
  extra: Record<string, unknown>;
};

/** Normalized CSV header → DB column (excluding `extra`, `linkedin_slug` — derived). */
const HEADER_TO_COLUMN: Record<string, keyof LinkedinUpsertRow> = {
  id: 'lead_delta_id',
  'lead delta id': 'lead_delta_id',
  'connection id': 'lead_delta_id',
  lead_delta_id: 'lead_delta_id',

  'first name': 'first_name',
  firstname: 'first_name',
  first_name: 'first_name',

  'last name': 'last_name',
  lastname: 'last_name',
  last_name: 'last_name',

  name: 'full_name',
  'full name': 'full_name',
  fullname: 'full_name',
  full_name: 'full_name',

  'linkedin url': 'linkedin_url',
  'profile url': 'linkedin_url',
  linkedin: 'linkedin_url',
  linkedin_url: 'linkedin_url',

  email: 'email',
  'e-mail': 'email',

  'business email': 'business_email',
  business_email: 'business_email',

  'personal email': 'personal_email',
  personal_email: 'personal_email',

  phone: 'phone',
  'phone number': 'phone',
  mobile: 'phone',

  headline: 'headline',
  title: 'headline',
  position: 'headline',
  'job title': 'headline',

  company: 'company_name',
  'company name': 'company_name',
  organization: 'company_name',
  employer: 'company_name',
  company_name: 'company_name',

  location: 'location',
  geography: 'location',

  country: 'country',

  industry: 'industry',

  'date connected': 'connected_on',
  'connected on': 'connected_on',
  connected: 'connected_on',
  connectiondate: 'connected_on',
  connected_on: 'connected_on',

  followers: 'followers',
  follower_count: 'followers',
  'follower count': 'followers',

  languages: 'languages',
  language: 'languages',

  'date of birth': 'date_of_birth',
  dateofbirth: 'date_of_birth',
  dob: 'date_of_birth',
  birthday: 'date_of_birth',
  date_of_birth: 'date_of_birth',

  'last messaged': 'last_messaged',
  lastmessaged: 'last_messaged',
  last_messaged: 'last_messaged',
  'last message': 'last_messaged',

  tags: 'tags',
  tag: 'tags',

  notes: 'notes',
  note: 'notes',

  'profile picture': 'profile_picture_url',
  'profile picture url': 'profile_picture_url',
  profile_picture_url: 'profile_picture_url',
  'photo url': 'profile_picture_url',
  'picture url': 'profile_picture_url',
  'profile image': 'profile_picture_url',
  'profile image url': 'profile_picture_url',
  'avatar url': 'profile_picture_url',
  photo: 'profile_picture_url',
  picture: 'profile_picture_url',
};

function normHeader(h: string): string {
  let s = h.trim();
  if (s.charCodeAt(0) === 0xfeff) s = s.slice(1);
  return s.replace(/\s+/g, ' ').toLowerCase();
}

/** If LinkedIn URL is only in unmapped columns (e.g. exact header "LinkedIn"). */
function pullLinkedinUrlFromExtra(extra: Record<string, unknown>): string | null {
  for (const [k, v] of Object.entries(extra)) {
    if (/^linkedin$/i.test(k.trim()) && typeof v === 'string') {
      const t = v.trim();
      if (t.length) return t;
    }
  }
  return null;
}

export function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let i = 0;
  let inQuotes = false;
  const len = text.length;

  while (i < len) {
    const c = text[i]!;
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += c;
      i++;
      continue;
    }
    if (c === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (c === ',') {
      row.push(field);
      field = '';
      i++;
      continue;
    }
    if (c === '\n') {
      row.push(field);
      field = '';
      if (row.some((cell) => cell.length > 0)) rows.push(row);
      row = [];
      i++;
      continue;
    }
    if (c === '\r') {
      if (text[i + 1] === '\n') {
        row.push(field);
        field = '';
        if (row.some((cell) => cell.length > 0)) rows.push(row);
        row = [];
        i += 2;
        continue;
      }
      i++;
      continue;
    }
    field += c;
    i++;
  }
  row.push(field);
  if (row.some((cell) => cell.length > 0)) rows.push(row);

  if (rows.length === 0) return { headers: [], rows: [] };
  const headers = rows[0]!.map((h) => (h.charCodeAt(0) === 0xfeff ? h.slice(1) : h));
  const dataRows = rows.slice(1).filter((r) => r.some((c) => c.trim().length > 0));
  return { headers, rows: dataRows };
}

function emptyToNull(s: string): string | null {
  const t = s.trim();
  return t.length ? t : null;
}

function parseFollowers(s: string): number | null {
  const t = s.replace(/[,.\s]/g, '').trim();
  if (!t) return null;
  const n = Number.parseInt(t, 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * Parse CSV date/time for Postgres `timestamptz`. Uses **local** calendar semantics for
 * Lead Delta-style `YYYY-MM-DD` and `YYYY-MM-DD HH:mm` so "2023-06-01 23:32" does not drift
 * across timezones. Result is always an ISO-8601 string (UTC instant) for PostgREST.
 */
function parseTimestamp(s: string): string | null {
  const t = s.trim();
  if (!t) return null;

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/;
  const dm = t.match(dateOnly);
  if (dm) {
    const y = Number(dm[1]);
    const mo = Number(dm[2]) - 1;
    const d = Number(dm[3]);
    const dt = new Date(y, mo, d, 0, 0, 0, 0);
    if (Number.isNaN(dt.getTime())) return null;
    return dt.toISOString();
  }

  const spaced = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.(\d{1,3}))?$/;
  const sm = t.match(spaced);
  if (sm) {
    const y = Number(sm[1]);
    const mo = Number(sm[2]) - 1;
    const d = Number(sm[3]);
    const h = Number(sm[4]);
    const min = Number(sm[5]);
    const sec = sm[6] != null ? Number(sm[6]) : 0;
    const dt = new Date(y, mo, d, h, min, sec, 0);
    if (Number.isNaN(dt.getTime())) return null;
    return dt.toISOString();
  }

  const fallback = new Date(t);
  if (Number.isNaN(fallback.getTime())) return null;
  return fallback.toISOString();
}

/** YYYY-MM-DD for Postgres `date` (local calendar day, not UTC midnight shift). */
function parseDateOnly(s: string): string | null {
  const t = s.trim();
  if (!t) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    const dt = new Date(y, mo, d, 12, 0, 0, 0);
    if (Number.isNaN(dt.getTime()) || dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) {
      return null;
    }
    const mm = String(mo + 1).padStart(2, '0');
    const dd = String(d).padStart(2, '0');
    return `${y}-${mm}-${dd}`;
  }
  const fallback = new Date(t);
  if (Number.isNaN(fallback.getTime())) return null;
  return fallback.toISOString().slice(0, 10);
}

function buildEmptyRow(): LinkedinUpsertRow {
  return {
    linkedin_slug: '',
    lead_delta_id: null,
    first_name: null,
    last_name: null,
    full_name: null,
    linkedin_url: null,
    email: null,
    business_email: null,
    personal_email: null,
    phone: null,
    headline: null,
    company_name: null,
    location: null,
    country: null,
    industry: null,
    connected_on: null,
    followers: null,
    languages: null,
    date_of_birth: null,
    last_messaged: null,
    tags: null,
    notes: null,
    profile_picture_url: null,
    extra: {},
  };
}

function setField(rec: LinkedinUpsertRow, col: keyof LinkedinUpsertRow, raw: string): void {
  const v = emptyToNull(raw);
  if (col === 'lead_delta_id') {
    rec.lead_delta_id = v;
    return;
  }
  if (col === 'linkedin_slug') {
    return;
  }
  if (col === 'profile_picture_url') {
    rec.profile_picture_url = v;
    return;
  }
  if (col === 'followers') {
    rec.followers = raw.trim() ? parseFollowers(raw) : null;
    return;
  }
  if (col === 'connected_on' || col === 'last_messaged') {
    rec[col] = raw.trim() ? parseTimestamp(raw) : null;
    return;
  }
  if (col === 'date_of_birth') {
    rec.date_of_birth = raw.trim() ? parseDateOnly(raw) : null;
    return;
  }
  if (col === 'extra') return;
  (rec as Record<string, unknown>)[col] = v;
}

export type CsvImportStats = {
  imported: number;
  skippedNoProfile: number;
  skippedEmpty: number;
  /** CSV rows that shared the same /in/ slug; later row wins (required for Postgres upsert batches). */
  csvDuplicateSlugs: number;
};

/**
 * Map parsed CSV (headers + row cells) to upsert records. Unmapped columns go to `extra`.
 * Rows without a parsable LinkedIn /in/ slug are skipped.
 */
export function rowsToLinkedinUpserts(headers: string[], rowCells: string[][]): {
  records: LinkedinUpsertRow[];
  stats: CsvImportStats;
} {
  const normHeaders = headers.map(normHeader);
  const colByIndex = normHeaders.map((nh) => HEADER_TO_COLUMN[nh] ?? null);

  const records: LinkedinUpsertRow[] = [];
  let skippedNoProfile = 0;
  let skippedEmpty = 0;

  for (const cells of rowCells) {
    const rec = buildEmptyRow();
    const extra: Record<string, unknown> = {};

    for (let ci = 0; ci < headers.length; ci++) {
      const rawHeader = headers[ci]!;
      const cell = cells[ci] ?? '';
      const col = colByIndex[ci];
      if (col != null && col !== 'extra' && col !== 'linkedin_slug') {
        setField(rec, col, cell);
      } else {
        const key = rawHeader.trim() || `column_${ci}`;
        const v = cell.trim();
        if (v.length) extra[key] = v;
      }
    }

    rec.extra = extra;

    if (!rec.linkedin_url?.trim()) {
      const fromExtra = pullLinkedinUrlFromExtra(extra);
      if (fromExtra) rec.linkedin_url = fromExtra;
    }

    const slug = extractLinkedinProfileSlug(rec.linkedin_url);
    if (!slug) {
      const allEmpty = cells.every((c) => !c.trim());
      if (allEmpty) skippedEmpty++;
      else skippedNoProfile++;
      continue;
    }

    rec.linkedin_slug = slug;
    if (!rec.profile_picture_url?.trim()) {
      rec.profile_picture_url = `https://unavatar.io/linkedin.com/in/${encodeURIComponent(slug)}`;
    }
    records.push(rec);
  }

  // A single upsert request cannot include the same primary key twice — Postgres errors (often as HTTP 500 from PostgREST).
  const bySlug = new Map<string, LinkedinUpsertRow>();
  for (const rec of records) {
    bySlug.set(rec.linkedin_slug, rec);
  }
  const deduped = Array.from(bySlug.values());
  const csvDuplicateSlugs = records.length - deduped.length;

  return {
    records: deduped,
    stats: {
      imported: deduped.length,
      skippedNoProfile,
      skippedEmpty,
      csvDuplicateSlugs,
    },
  };
}
